import {
  ShippingMethodCategory,
  ShippingMethodConfiguration,
} from '@monofront/types';
import {
  FormattedShippingMethodsThresholds,
  ThresholdData,
  ThresholdPriceData,
} from './types';
import { boil, flat, sift, unique } from 'radash';
import { DeliveryBarMachineContextShippingMethod } from '@monofront/business/stateMachines/deliveryBar/types';

const findCheapestAboveAllThresholdsAmount = (
  shippingMethods: ShippingMethodConfiguration[],
) =>
  boil(shippingMethods, (a, b) => {
    if (!b) return a;

    return a.aboveAllThresholdsAmount < b.aboveAllThresholdsAmount ? a : b;
  })?.aboveAllThresholdsAmount || 0;

const removeDuplicateThresholds = (thresholds) => {
  if (!Array.isArray(thresholds)) return [];

  const thresholdMap = new Map();

  thresholds.forEach((threshold) => {
    const { threshold: key, underThresholdAmount } = threshold;

    if (
      !thresholdMap.has(key) ||
      thresholdMap.get(key).underThresholdAmount > underThresholdAmount
    ) {
      thresholdMap.set(key, threshold);
    }
  });

  return Array.from(thresholdMap.values());
};

const sanitizeThresholdsData = (
  shippingMethods: ShippingMethodConfiguration[],
  globalThresholds: number[],
  aboveAllThresholdAmount: number,
): ThresholdPriceData[] => {
  const thresholds = sift(
    shippingMethods.map((item) => item.thresholds).flat(),
  ).sort((a, b) => a.threshold - b.threshold);

  if (!thresholds.length) return [];

  const highestThreshold = thresholds[thresholds.length - 1];

  globalThresholds.forEach((globalThreshold) => {
    if (
      !thresholds.some((threshold) => threshold.threshold === globalThreshold)
    ) {
      const closestThreshold = thresholds.reduce((prev, curr) =>
        Math.abs(curr.threshold - globalThreshold) <
        Math.abs(prev.threshold - globalThreshold)
          ? curr
          : prev,
      ).underThresholdAmount;

      thresholds.push({
        threshold: globalThreshold,
        underThresholdAmount:
          globalThreshold > highestThreshold.threshold
            ? aboveAllThresholdAmount
            : closestThreshold,
      });
    }
  });

  return removeDuplicateThresholds(thresholds);
};

export const formatShippingMethodsThresholds = (
  shippingMethod: DeliveryBarMachineContextShippingMethod,
  minimumOrderAmount: number,
): FormattedShippingMethodsThresholds => {
  const emptyResult: FormattedShippingMethodsThresholds = {
    thresholdCol: [],
    standardThresholds: [],
    ultraFreshThresholds: [],
  };

  if (!shippingMethod.configurations) {
    return emptyResult;
  }

  const configurations = shippingMethod.configurations.map((config) => ({
    ...config,
    thresholds: (config.thresholds ?? []).filter(
      (configuration) => configuration.threshold >= minimumOrderAmount,
    ),
  }));

  const thresholds = sift(configurations.map((item) => item.thresholds));

  const uniqueThresholds = unique(flat(thresholds), (f) => f.threshold)
    .map(({ threshold }) => threshold)
    .sort((a, b) => a - b);

  uniqueThresholds.push(Infinity);

  const standardShippingMethods = configurations.filter(
    (configuration) =>
      !configuration.category ||
      [ShippingMethodCategory.DEFAULT, ShippingMethodCategory.FRESH].includes(
        configuration.category as ShippingMethodCategory,
      ),
  );

  const cheapestStandardAboveAllThresholdsAmount =
    findCheapestAboveAllThresholdsAmount(standardShippingMethods);

  const standardThresholds: ThresholdPriceData[] = sanitizeThresholdsData(
    standardShippingMethods,
    uniqueThresholds,
    cheapestStandardAboveAllThresholdsAmount,
  ).map((threshold) => ({
    ...threshold,
    accessorKey: ShippingMethodCategory.DEFAULT,
    aboveAllThresholdsAmount: cheapestStandardAboveAllThresholdsAmount,
  }));

  const ultraFreshShippingMethods = configurations.filter((configuration) =>
    [ShippingMethodCategory.ULTRA_FRESH].includes(
      configuration.category as ShippingMethodCategory,
    ),
  );
  const cheapestUltraFreshAboveAllThresholdsAmount =
    findCheapestAboveAllThresholdsAmount(ultraFreshShippingMethods);

  const ultraFreshThresholds: ThresholdPriceData[] = sanitizeThresholdsData(
    ultraFreshShippingMethods,
    uniqueThresholds,
    cheapestUltraFreshAboveAllThresholdsAmount,
  ).map((threshold) => ({
    ...threshold,
    accessorKey: ShippingMethodCategory.ULTRA_FRESH,
    aboveAllThresholdsAmount: cheapestUltraFreshAboveAllThresholdsAmount,
  }));

  const thresholdCol: ThresholdData[] = uniqueThresholds.map((threshold) => ({
    accessorKey: 'threshold',
    value: threshold,
  }));

  return {
    thresholdCol,
    standardThresholds,
    ultraFreshThresholds,
  };
};
